import { useState } from "react";
import { Play } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import { durationLabel, videoCardUrl, videoEmbedUrl, videoThumbUrl, type PostVideo } from "@/data/postVideos";

/**
 * Click-to-play facade for an article's summary video. Nothing is requested
 * from YouTube until the visitor plays it: the facade is a navy portrait box
 * holding the video's own thumbnail, so an article with a video costs the page
 * no third-party weight on load.
 *
 * The box sits inside the article column's 760px measure and is left-aligned
 * with the text on desktop, full width on mobile.
 */
export function ArticleVideo({ slug, video }: { slug: string; video: PostVideo }) {
  const [playing, setPlaying] = useState(false);
  const [thumb, setThumb] = useState(videoThumbUrl(video.youtubeId));
  const label = durationLabel(video.durationIso);

  return (
    <div className="mx-auto max-w-[760px]">
      <span className="eyebrow">Watch the summary{label ? ` (${label})` : ""}</span>
      <div className="mt-4 aspect-[9/16] w-full max-w-[360px] overflow-hidden rounded-2xl border border-border bg-midnight">
        {playing ? (
          <iframe
            className="h-full w-full"
            src={`${videoEmbedUrl(video.youtubeId)}?autoplay=1&rel=0&modestbranding=1&playsinline=1`}
            title={video.title}
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
            loading="lazy"
          />
        ) : (
          <button
            type="button"
            onClick={() => {
              setPlaying(true);
              trackEvent("insight_video_play", { slug, youtubeId: video.youtubeId });
            }}
            aria-label={`Play the summary video: ${video.title}`}
            className="group relative block h-full w-full cursor-pointer"
          >
            <img
              src={thumb}
              alt=""
              loading="lazy"
              onError={() => setThumb(videoCardUrl(video.youtubeId))}
              className="absolute inset-0 h-full w-full object-cover"
            />
            <span className="absolute inset-0 bg-midnight/20 transition group-hover:bg-midnight/5" />
            <span className="absolute left-1/2 top-1/2 grid h-16 w-16 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-frost shadow-card transition group-hover:scale-105">
              <Play className="ml-1 h-6 w-6 text-midnight" aria-hidden="true" />
            </span>
          </button>
        )}
      </div>
      <p className="mt-3 text-sm text-muted-foreground">Prefer to read? The full guide is below.</p>
    </div>
  );
}
